// src/components/admin/Order.jsx
import React from "react";
import { useQuery } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { Link, useNavigate } from "react-router-dom";

const Order = () => {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;
  const token = Cookies.get("gameport");

  const {
    data,
    isPending,
    isError,
    error,
  } = useQuery({
    queryKey: ["admin-recent-orders"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/order/all-orders`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || "Failed to fetch orders");
      return json;
    },
  });

  const orders = data?.orders?.slice(0, 3) ?? [];

  if (isPending) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6">
        <p className="text-sm text-gray-600">Loading recent orders...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6">
        <p className="text-sm text-red-600">{error?.message}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-4">
        <h2 className="text-base sm:text-lg font-semibold text-gray-800">
          Recent Orders
        </h2>

        <Link
          to="/all-orders"
          className="inline-flex items-center justify-center rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 transition w-full sm:w-auto"
        >
          View All
        </Link>
      </div>

      {/* MOBILE VIEW (cards) */}
      <div className="grid grid-cols-1 gap-3 sm:hidden">
        {orders.length === 0 ? (
          <div className="text-sm text-gray-600 py-4 text-center">
            No recent orders.
          </div>
        ) : (
          orders.map((order) => (
            <button
              key={order._id}
              onClick={() => navigate(`/admin/orders/${order._id}`)}
              className="text-left rounded-2xl border border-gray-100 bg-gray-50 p-4 active:scale-[0.99] transition"
            >
              <div className="flex justify-between items-start gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900 truncate">
                    #{order.orderCode}
                  </p>
                  <p className="text-sm text-gray-600 truncate">
                    {order?.user?.fullName || "Unknown"}
                  </p>
                </div>

                <span className="text-xs font-semibold capitalize rounded-full bg-white border px-2 py-1 text-green-700">
                  {order.orderStatus}
                </span>
              </div>

              <p className="mt-2 font-semibold text-gray-900">
                ₦{Number(order.total).toLocaleString()}
              </p>
            </button>
          ))
        )}
      </div>

      {/* DESKTOP VIEW (table) */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full text-sm min-w-[600px]">
          <thead className="border-b text-gray-500">
            <tr>
              <th className="text-left py-3 pr-4 font-medium">Order ID</th>
              <th className="text-left py-3 pr-4 font-medium">Customer</th>
              <th className="text-left py-3 pr-4 font-medium">Status</th>
              <th className="text-left py-3 font-medium">Amount</th>
            </tr>
          </thead>

          <tbody>
            {orders.length === 0 ? (
              <tr>
                <td colSpan={4} className="py-6 text-center text-gray-600">
                  No recent orders.
                </td>
              </tr>
            ) : (
              orders.map((order) => (
                <tr
                  key={order._id}
                  onClick={() => navigate(`/admin/orders/${order._id}`)}
                  className="border-b hover:bg-gray-50 cursor-pointer"
                >
                  <td className="py-3 pr-4 font-medium text-gray-900">
                    #{order.orderCode}
                  </td>
                  <td className="py-3 pr-4 text-gray-700">
                    {order?.user?.fullName || "Unknown"}
                  </td>
                  <td className="py-3 pr-4 text-green-600 capitalize font-medium">
                    {order.orderStatus}
                  </td>
                  <td className="py-3 text-gray-900 font-semibold">
                    ₦{Number(order.total).toLocaleString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Order;
